import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiMail, FiEdit3, FiMenu, FiBriefcase, FiUser, FiSettings, FiUpload, FiCheckCircle, FiX, FiUserPlus, FiChevronDown, FiUsers } from 'react-icons/fi';
import nacmeLogo from './assets/nacme_logo.jpg';
import tlaLogo from './assets/tla_logo.png';
import { IS_TLA, PATHS, getAlumniPath } from './utils/platformPaths';
import { SkillsDisplay, ExperienceDisplay, EducationDisplay, LocationDisplay } from './components/LinkedInDataDisplay';
import EditableSkills from './components/EditableSkills';
import PeopleYouMayKnow from './PeopleYouMayKnow';
import { MobileAlumniNav, MobileAlumniBottomNav } from './components/AlumniMobileComponents';
import PostCreator from './components/PostCreator';
import AlumniNewsFeed from './components/AlumniNewsFeed';
import { useWebSocket } from './hooks/useWebSocket';
import { AlumniBrokeragePanel } from './components/brokerage';

const AlumniDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [resumeFile, setResumeFile] = useState(null);
  const [isUploadingResume, setIsUploadingResume] = useState(false);
  const [headshotFile, setHeadshotFile] = useState(null);
  const [isUploadingHeadshot, setIsUploadingHeadshot] = useState(false);
  const [backgroundFile, setBackgroundFile] = useState(null);
  const [isUploadingBackground, setIsUploadingBackground] = useState(false);
  
  // LinkedIn Import State
  const [linkedinInput, setLinkedinInput] = useState('');
  const [isPreviewingLinkedin, setIsPreviewingLinkedin] = useState(false);
  const [isImportingLinkedin, setIsImportingLinkedin] = useState(false);
  const [linkedinPreview, setLinkedinPreview] = useState(null);
  const [showLinkedinModal, setShowLinkedinModal] = useState(false);
  const [linkedinProgress, setLinkedinProgress] = useState({
    step: '',
    message: '',
    showProgress: false
  });
  
  // Profile completion popup state
  const [showCompletionPopup, setShowCompletionPopup] = useState(false);
  
  const [editData, setEditData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    graduation_year: '',
    degree_type: '',
    major: '',
    university: '',
    current_employer: '',
    current_position: '',
    years_experience: '',
    linkedin_url: '',
    about: ''
  });
  
  // Privacy settings state
  const [privacySettings, setPrivacySettings] = useState({
    social_networking_enabled: true,
    show_current_employer: true,
    show_degree_type: true,
    show_major: true,
    show_graduation_year: true,
    show_university: true,
    show_current_position: true,
    show_years_experience: false,
    show_location: false,
    show_skills: false,
    show_about: true,
    show_work_experience: true
  });
  const [showPrivacySettings, setShowPrivacySettings] = useState(false);
  const [privacyLoading, setPrivacyLoading] = useState(false);
  
  // Alumni Network dropdown state
  const [showAlumniMenu, setShowAlumniMenu] = useState(false);
  const dropdownRef = useRef(null);
  
  // File input refs
  const headshotInputRef = useRef(null);
  const backgroundInputRef = useRef(null);
  
  // Unread messages count state
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Pending connection requests count state
  const [pendingConnectionCount, setPendingConnectionCount] = useState(0);
  
  // Mobile state
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);

  // Connection management state
  const [pendingConnections, setPendingConnections] = useState([]);
  const [myConnections, setMyConnections] = useState([]);
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [connectionError, setConnectionError] = useState('');

  // Mobile collapsible sections state
  const [isPeopleYouMayKnowCollapsed, setIsPeopleYouMayKnowCollapsed] = useState(true);
  const [isMyConnectionsCollapsed, setIsMyConnectionsCollapsed] = useState(true);
  const [isBrokerageCollapsed, setIsBrokerageCollapsed] = useState(false);

  // Alumni token for brokerage panel
  const alumniToken = localStorage.getItem('token');

  // Upgrade success banner (from HubSpot payment redirect)
  const [showUpgradeBanner, setShowUpgradeBanner] = useState(false);

  useEffect(() => {
    if (searchParams.get('upgrade') === 'success') {
      setShowUpgradeBanner(true);
      // Clean the URL so refreshing doesn't re-show the banner
      searchParams.delete('upgrade');
      setSearchParams(searchParams, { replace: true });
      // Auto-dismiss after 15 seconds
      const timer = setTimeout(() => setShowUpgradeBanner(false), 15000);
      return () => clearTimeout(timer);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // WebSocket connection
  const { isConnected, lastMessage, connectionError: wsConnectionError } = useWebSocket(profile?.id);
  const [newPostNotification, setNewPostNotification] = useState(null);
  const [newPostForFeed, setNewPostForFeed] = useState(null);
  const feedRef = useRef(null);

  const degreeTypes = [
    { value: '', label: 'Select Degree Type' },
    { value: 'bachelors', label: "Bachelor's Degree" },
    { value: 'masters', label: "Master's Degree" },
    { value: 'phd', label: 'Ph.D.' },
    { value: 'jd', label: 'J.D. (Juris Doctor)' },
    { value: 'mba', label: 'M.B.A. (Master of Business Administration)' },
    { value: 'edd', label: 'Ed.D. (Doctor of Education)' },
    { value: 'md', label: 'M.D. (Doctor of Medicine)' },
    { value: 'deng', label: 'D.Eng. (Doctor of Engineering)' }
  ];

  // Check profile completion status and generate appropriate message
  const getProfileCompletionStatus = () => {
    if (!profile) return { isComplete: false, message: '', tasks: [] };

    const hasPhoto = !!profile?.headshot_url;
    const hasResume = !!profile?.resume_url;
    const hasLinkedIn = !!(profile?.skills_list && profile?.skills_list.length > 0);
    const hasVerifiedEmail = !!profile?.email_verified;

    const missingTasks = [];
    if (!hasVerifiedEmail) missingTasks.push('verify your email address');
    if (!hasPhoto) missingTasks.push('upload your photo');
    if (!hasResume) missingTasks.push('upload your resume');
    if (!hasLinkedIn) missingTasks.push('import your LinkedIn profile');

    const isComplete = missingTasks.length === 0;

    let message = '';
    if (!isComplete) {
      if (missingTasks.length === 4) {
        message = "Please verify your email address, upload your photo, resume, import your profile--and you're off to the races!";
      } else if (missingTasks.length === 3) {
        const lastTask = missingTasks.pop();
        message = `Please ${missingTasks.join(', ')}, and ${lastTask} to get started.`;
      } else if (missingTasks.length === 2) {
        message = `Please ${missingTasks[0]} and ${missingTasks[1]} to get started.`;
      } else {
        message = `Please ${missingTasks[0]} to complete your profile.`;
      }
    }

    return {
      isComplete,
      message,
      tasks: missingTasks,
      hasPhoto,
      hasResume,
      hasLinkedIn
    };
  };

  useEffect(() => {
    fetchProfile();
    fetchUnreadMessageCount();
  }, []);

  // Refresh unread count when returning to page
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchUnreadMessageCount();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Show completion popup only on first login, not on every dashboard visit
  useEffect(() => {
    if (profile && !isLoading) {
      const completionStatus = getProfileCompletionStatus();
      const hasShownPopupThisSession = localStorage.getItem('alumni_completion_popup_shown');
      
      if (!completionStatus.isComplete && !hasShownPopupThisSession) {
        // Show popup after a short delay for better UX
        const timer = setTimeout(() => {
          setShowCompletionPopup(true);
          // Mark as shown for this session
          localStorage.setItem('alumni_completion_popup_shown', 'true');
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [profile, isLoading]);

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowAlumniMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchProfile = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate(PATHS.login);
        return;
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/api/alumni/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          navigate(PATHS.login);
          return;
        }
        throw new Error('Failed to fetch profile');
      }

      const data = await response.json();
      setProfile(data);
      setEditData({
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        phone: data.phone || '',
        graduation_year: data.graduation_year || '',
        degree_type: data.degree_type || '',
        major: data.major || '',
        university: data.university || '',
        current_employer: data.current_employer || '',
        current_position: data.current_position || '',
        years_experience: data.years_experience || '',
        linkedin_url: data.linkedin_url || '',
        about: data.about || ''
      });
      
      // Load privacy settings
      fetchPrivacySettings();
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Failed to load profile');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPrivacySettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      
      const response = await fetch(`${backendUrl}/api/alumni/privacy`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPrivacySettings(data);
      }
    } catch (error) {
      console.error('Error fetching privacy settings:', error);
    }
  };

  const handlePrivacySettingChange = (setting, value) => {
    setPrivacySettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  // Social component callbacks
  const handlePostCreated = (newPost) => {
    console.log('New post created:', newPost);
    setSuccessMessage('Post created successfully!');
    setTimeout(() => setSuccessMessage(''), 3000);
    
    // Automatically refresh the feed to show the new post
    setTimeout(() => {
      handleFeedRefresh();
    }, 500); // Small delay to ensure backend processing is complete
  };

  const handleFeedError = (error) => {
    console.error('Feed error:', error);
    setError(error);
    setTimeout(() => setError(''), 5000);
  };

  const handleFeedRefresh = () => {
    // Trigger a refresh of the AlumniNewsFeed component
    // We'll do this by dispatching a custom event that the feed component can listen for
    const refreshEvent = new CustomEvent('refreshFeed', { 
      detail: { timestamp: Date.now() } 
    });
    window.dispatchEvent(refreshEvent);
    console.log('Feed refresh triggered');
  };

  const savePrivacySettings = async () => {
    setPrivacyLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      
      const response = await fetch(`${backendUrl}/api/alumni/privacy`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(privacySettings)
      });

      if (response.ok) {
        setSuccessMessage('Privacy settings updated successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        throw new Error('Failed to update privacy settings');
      }
    } catch (error) {
      console.error('Error saving privacy settings:', error);
      setError('Failed to save privacy settings');
    } finally {
      setPrivacyLoading(false);
    }
  };

  // Fetch unread message count
  const fetchUnreadMessageCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      
      const response = await fetch(`${backendUrl}/api/alumni/messages`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const unread = data.received ? data.received.filter(msg => !msg.read_at).length : 0;
        setUnreadCount(unread);
      }
    } catch (error) {
      console.error('Error fetching unread message count:', error);
      // Don't show error to user for message count - it's not critical
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData({
      ...editData,
      [name]: value
    });
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      
      // Prepare data
      const submitData = { ...editData };
      
      // Convert to numbers where needed
      if (submitData.graduation_year) {
        submitData.graduation_year = parseInt(submitData.graduation_year);
      }
      if (submitData.years_experience) {
        submitData.years_experience = parseInt(submitData.years_experience);
      }
      
      // Remove empty fields
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === '') {
          delete submitData[key];
        }
      });

      const response = await fetch(`${backendUrl}/api/alumni/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      if (!response.ok) {
        throw new Error('Failed to update profile');
      }

      const data = await response.json();
      setProfile(data);
      setIsEditing(false);
      setSuccessMessage('Profile updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile');
    }
  };

  const handleResumeUpload = async (e) => {
    e.preventDefault();
    if (!resumeFile) {
      setError('Please select a file to upload');
      return;
    }

    setIsUploadingResume(true);
    setError('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      
      const formData = new FormData();
      formData.append('file', resumeFile);

      const response = await fetch(`${backendUrl}/api/alumni/upload-resume`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to upload resume');
      }

      const data = await response.json();
      setSuccessMessage('Resume uploaded successfully!');
      setResumeFile(null);
      // Update profile to reflect new resume
      await fetchProfile();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error uploading resume:', err);
      setError(err.message || 'Failed to upload resume');
    } finally {
      setIsUploadingResume(false);
    }
  };

  const handleHeadshotUpload = async (e) => {
    e.preventDefault();
    if (!headshotFile) {
      setError('Please select a file to upload');
      return;
    }

    setIsUploadingHeadshot(true);
    setError('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      
      const formData = new FormData();
      formData.append('file', headshotFile);

      const response = await fetch(`${backendUrl}/api/alumni/upload-headshot`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to upload headshot');
      }

      const data = await response.json();
      setSuccessMessage('Headshot uploaded successfully!');
      setHeadshotFile(null);
      // Update profile to reflect new headshot
      await fetchProfile();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error uploading headshot:', err);
      setError(err.message || 'Failed to upload headshot');
    } finally {
      setIsUploadingHeadshot(false);
    }
  };

  const handleBackgroundUpload = async (e) => {
    e.preventDefault();
    if (!backgroundFile) {
      setError('Please select a file to upload');
      return;
    }

    setIsUploadingBackground(true);
    setError('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      
      const formData = new FormData();
      formData.append('file', backgroundFile);

      const response = await fetch(`${backendUrl}/api/alumni/upload-background`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to upload background image');
      }

      const data = await response.json();
      setSuccessMessage('Background image uploaded successfully!');
      setBackgroundFile(null);
      // Update profile to reflect new background image
      await fetchProfile();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error uploading background image:', err);
      setError(err.message || 'Failed to upload background image');
    } finally {
      setIsUploadingBackground(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    localStorage.removeItem('alumniData');
    localStorage.removeItem('alumni_completion_popup_shown');
    navigate(PATHS.login);
  };

  // LinkedIn Import Functions
  const handleLinkedinPreview = async () => {
    if (!linkedinInput.trim()) {
      setError('Please enter a LinkedIn URL or username');
      return;
    }

    setIsPreviewingLinkedin(true);
    setLinkedinProgress({
      step: 1,
      message: 'Initializing LinkedIn scraper...',
      showProgress: true
    });
    setError('');

    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

      // Update progress
      setLinkedinProgress({
        step: 2,
        message: 'Connecting to import your Profile...',
        showProgress: true
      });

      const response = await fetch(`${backendUrl}/api/alumni/linkedin/preview`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ linkedin_input: linkedinInput })
      });

      // Update progress
      setLinkedinProgress({
        step: 3,
        message: 'Scraping LinkedIn profile data...',
        showProgress: true
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to preview LinkedIn profile');
      }

      // Update progress
      setLinkedinProgress({
        step: 4,
        message: 'Processing profile information...',
        showProgress: true
      });

      const data = await response.json();
      setLinkedinPreview(data.preview);
      setSuccessMessage('LinkedIn profile preview loaded successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error previewing LinkedIn profile:', err);
      setError(err.message || 'Failed to preview LinkedIn profile');
    } finally {
      setIsPreviewingLinkedin(false);
      setLinkedinProgress({
        step: '',
        message: '',
        showProgress: false
      });
    }
  };

  const handleLinkedinImport = async () => {
    if (!linkedinInput.trim()) {
      setError('Please enter a LinkedIn URL or username');
      return;
    }

    setIsImportingLinkedin(true);
    setLinkedinProgress({
      step: 1,
      message: 'Re-fetching LinkedIn data...',
      showProgress: true
    });
    setError('');

    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

      // Update progress
      setLinkedinProgress({
        step: 2,
        message: 'Mapping data to profile fields...',
        showProgress: true
      });

      const response = await fetch(`${backendUrl}/api/alumni/linkedin/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ linkedin_input: linkedinInput })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to import LinkedIn profile');
      }

      // Update progress
      setLinkedinProgress({
        step: 3,
        message: 'Updating your profile...',
        showProgress: true
      });

      const data = await response.json();
      
      // Update progress
      setLinkedinProgress({
        step: 4,
        message: 'Refreshing dashboard...',
        showProgress: true
      });

      setSuccessMessage(`Successfully imported ${data.imported_fields?.length || 0} fields from LinkedIn!`);
      setLinkedinInput('');
      setLinkedinPreview(null);
      setShowLinkedinModal(false);
      // Refresh profile to show updated data
      await fetchProfile();
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Error importing LinkedIn profile:', err);
      setError(err.message || 'Failed to import LinkedIn profile');
    } finally {
      setIsImportingLinkedin(false);
      setLinkedinProgress({
        step: '',
        message: '',
        showProgress: false
      });
    }
  };

  // Connection management functions
  const fetchPendingConnections = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/api/alumni/connections/pending`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPendingConnections(data.received || []);
        setPendingConnectionCount(data.received?.length || 0);
      }
    } catch (error) {
      console.error('Error fetching pending connections:', error);
      setConnectionError('Failed to load pending connections');
    }
  };

  const fetchMyConnections = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/api/alumni/connections`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMyConnections(data.connections || []);
      }
    } catch (error) {
      console.error('Error fetching connections:', error);
      setConnectionError('Failed to load connections');
    }
  };

  const handleConnectionResponse = async (connectionId, status) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/api/alumni/connections/respond`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          request_id: connectionId, 
          action: status === 'accepted' ? 'accept' : status === 'declined' ? 'decline' : status 
        })
      });

      if (response.ok) {
        setSuccessMessage(status === 'accepted' ? 'Connection accepted!' : 'Connection declined.');
        setTimeout(() => setSuccessMessage(''), 3000);
        await fetchPendingConnections();
        await fetchMyConnections();
      } else {
        const errorData = await response.json();
        // Handle both string and object error responses
        let errorMessage = 'Failed to respond to connection';
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (errorData.detail && Array.isArray(errorData.detail)) {
          // Handle validation errors array
          errorMessage = errorData.detail.map(err => err.msg || err).join(', ');
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
        setConnectionError(errorMessage);
      }
    } catch (error) {
      console.error('Error responding to connection:', error);
      setConnectionError('Failed to respond to connection');
    }
  };

  const handleRemoveConnection = async (connectionId) => {
    if (!window.confirm('Are you sure you want to remove this connection?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/api/alumni/connections/${connectionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setSuccessMessage('Connection removed.');
        setTimeout(() => setSuccessMessage(''), 3000);
        await fetchMyConnections();
      } else {
        const errorData = await response.json();
        // Handle both string and object error responses
        let errorMessage = 'Failed to remove connection';
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (errorData.detail && Array.isArray(errorData.detail)) {
          // Handle validation errors array
          errorMessage = errorData.detail.map(err => err.msg || err).join(', ');
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
        setConnectionError(errorMessage);
      }
    } catch (error) {
      console.error('Error removing connection:', error);
      setConnectionError('Failed to remove connection');
    }
  };

  // Scroll to connection requests section
  const scrollToConnectionRequests = () => {
    // Check if we're on desktop or mobile and select the appropriate section
    const isMobile = window.innerWidth < 768; // md breakpoint
    
    if (isMobile) {
      // Mobile: scroll to mobile connection requests section
      const mobileElement = document.querySelector('[data-section="mobile-connection-requests"]');
      if (mobileElement) {
        mobileElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    } else {
      // Desktop: scroll to desktop connection requests section
      const desktopElement = document.querySelector('[data-section="desktop-connection-requests"]');
      if (desktopElement) {
        desktopElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
    
    // Fallback: try to find any connection requests section
    const fallbackElement = document.querySelector('[data-section="connection-requests"]');
    if (fallbackElement) {
      fallbackElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Handle incoming WebSocket messages
  useEffect(() => {
    if (!lastMessage) return;
    
    switch (lastMessage.type) {
      case 'new_post':
        // Show notification for new post
        setNewPostNotification(lastMessage.post);
        
        // Auto-hide notification after 15 seconds (longer time)
        setTimeout(() => setNewPostNotification(null), 15000);
        
        // Show success message
        setSuccessMessage(`New post from ${lastMessage.post.author_name}`);
        
        // Automatically add the new post to the feed
        setNewPostForFeed(lastMessage.post);
        
        // Clear the new post data after a brief moment (so component can process it)
        setTimeout(() => setNewPostForFeed(null), 1000);
        break;
        
      case 'connection_request':
        // Update pending connection count
        setPendingConnectionCount(prev => prev + 1);
        
        // Show notification
        setSuccessMessage(`New connection request from ${lastMessage.request.requester_name}`);
        
        // Refresh pending connections
        if (privacySettings.social_networking_enabled) {
          fetchPendingConnections();
        }
        break;
        
      case 'connection_established':
        // Initial connection confirmation
        console.log('WebSocket connected successfully');
        break;
        
      default:
        console.log('Unknown WebSocket message type:', lastMessage.type);
    }
  }, [lastMessage]);

  // Load connection data when dashboard loads
  useEffect(() => {
    if (profile && privacySettings.social_networking_enabled) {
      setLoadingConnections(true);
      Promise.all([fetchPendingConnections(), fetchMyConnections()])
        .finally(() => setLoadingConnections(false));
    }
  }, [profile, privacySettings.social_networking_enabled]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Navigation */}
      <div className="md:hidden">
        <MobileAlumniNav 
          title="Dashboard"
          onMenuClick={() => setShowMobileSidebar(true)}
        />
      </div>

      {/* Mobile Sidebar Menu */}
      {showMobileSidebar && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => setShowMobileSidebar(false)}
          />
          
          {/* Sidebar */}
          <div className="relative flex flex-col w-64 max-w-xs bg-white shadow-xl">
            {/* Sidebar Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center">
                <img className={`w-auto ${IS_TLA ? 'h-24' : 'h-8'}`} src={IS_TLA ? tlaLogo : nacmeLogo} alt={IS_TLA ? "TLA Logo" : "NACME Logo"} />
                <span className="ml-2 text-lg font-semibold text-gray-900">{IS_TLA ? 'TLA' : 'Alumni'}</span>
              </div>
              <button
                onClick={() => setShowMobileSidebar(false)}
                className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>

            {/* User Profile Section */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center">
                {profile?.headshot_url ? (
                  <img
                    src={profile.headshot_url}
                    alt="Profile"
                    className="h-12 w-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center">
                    <FiUser className="h-6 w-6 text-gray-400" />
                  </div>
                )}
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">
                    {profile?.first_name} {profile?.last_name}
                  </p>
                  <p className="text-xs text-gray-500">{profile?.email}</p>
                </div>
              </div>
            </div>

            {/* Navigation Links */}
            <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
              <button
                onClick={() => {
                  navigate(PATHS.dashboard);
                  setShowMobileSidebar(false);
                }}
                className="w-full flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-900 bg-gray-100"
              >
                <FiUser className="mr-3 h-5 w-5" />
                Dashboard
              </button>

              <button
                onClick={() => {
                  navigate(PATHS.jobBoard);
                  setShowMobileSidebar(false);
                }}
                className="w-full flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              >
                <FiBriefcase className="mr-3 h-5 w-5" />
                Job Board
                <span className="ml-auto bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full text-xs">
                  {/* Can add job count here */}
                  New
                </span>
              </button>

              <button
                onClick={() => {
                  navigate(PATHS.directory);
                  setShowMobileSidebar(false);
                }}
                className="w-full flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              >
                <svg className="mr-3 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Alumni Directory
              </button>

              {/* Connection Requests - Mobile Sidebar */}
              {privacySettings.social_networking_enabled && (
                <button
                  onClick={() => {
                    scrollToConnectionRequests();
                    setShowMobileSidebar(false);
                  }}
                  className="w-full flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                >
                  <FiUserPlus className="mr-3 h-5 w-5" />
                  Connection Requests
                  {pendingConnectionCount > 0 && (
                    <span className="ml-auto bg-red-500 text-white px-2 py-0.5 rounded-full text-xs">
                      {pendingConnectionCount}
                    </span>
                  )}
                </button>
              )}

              <button
                onClick={() => {
                  navigate(PATHS.messages);
                  setShowMobileSidebar(false);
                }}
                className="w-full flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              >
                <FiMail className="mr-3 h-5 w-5" />
                Messages
                {unreadCount > 0 && (
                  <span className="ml-auto bg-red-500 text-white px-2 py-0.5 rounded-full text-xs">
                    {unreadCount}
                  </span>
                )}
              </button>

              <button
                onClick={() => {
                  navigate(PATHS.groups);
                  setShowMobileSidebar(false);
                }}
                className="w-full flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              >
                <svg className="mr-3 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                Groups
              </button>

              <div className="my-3 border-t border-gray-200" />

              <button
                onClick={() => {
                  navigate(PATHS.profileManagement);
                  setShowMobileSidebar(false);
                }}
                className="w-full flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              >
                <FiSettings className="mr-3 h-5 w-5" />
                Profile Management
              </button>

              <button
                onClick={() => {
                  navigate(PATHS.resumeReview);
                  setShowMobileSidebar(false);
                }}
                className="w-full flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              >
                <FiCheckCircle className="mr-3 h-5 w-5" />
                Resume Review
              </button>

              <button
                onClick={() => {
                  setShowPrivacySettings(true);
                  setShowMobileSidebar(false);
                }}
                className="w-full flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              >
                <svg className="mr-3 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Privacy Settings
              </button>
            </nav>

            {/* Logout Button */}
            <div className="p-4 border-t border-gray-200">
              <button
                onClick={() => {
                  localStorage.removeItem('token');
                  navigate(PATHS.login);
                }}
                className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100"
              >
                <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Header */}
      <header className="hidden md:block bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <img className={`w-auto ${IS_TLA ? 'h-36' : 'h-12'}`} src={IS_TLA ? tlaLogo : nacmeLogo} alt={IS_TLA ? "TLA Logo" : "NACME Logo"} />
              <h1 className="ml-4 text-2xl font-bold text-gray-900">{IS_TLA ? 'TLA Dashboard' : 'Alumni Dashboard'}</h1>
            </div>
            <div className="flex items-center space-x-4">
              {/* Connection Requests Button - Blue with pending count */}
              {privacySettings.social_networking_enabled && (
                <button
                  onClick={scrollToConnectionRequests}
                  className="relative px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center"
                  title="Connection Requests"
                >
                  <FiUserPlus className="h-4 w-4" />
                  {pendingConnectionCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-medium px-1.5 py-0.5 rounded-full min-w-[1.25rem] flex items-center justify-center">
                      {pendingConnectionCount > 99 ? '99+' : pendingConnectionCount}
                    </span>
                  )}
                </button>
              )}

              {/* Messages Button - Gray with unread count */}
              <button
                onClick={() => navigate(PATHS.messages)}
                className="relative px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors flex items-center"
              >
                <FiMail className="h-4 w-4" />
                {unreadCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-medium px-1.5 py-0.5 rounded-full min-w-[1.25rem] flex items-center justify-center">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Browse Jobs Button - Purple */}
              <button
                onClick={() => navigate(PATHS.jobBoard)}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
              >
                Browse Jobs
              </button>

              {/* Resume Review Button - Green */}
              <button
                onClick={() => navigate(PATHS.resumeReview)}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                Resume Review
              </button>

              {/* Alumni Network Dropdown - Blue */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setShowAlumniMenu(!showAlumniMenu)}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Alumni Network
                  <svg className={`w-4 h-4 ml-2 transform transition-transform ${showAlumniMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {/* Dropdown Menu */}
                {showAlumniMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1">
                      <a
                        href={PATHS.directory}
                        className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        onClick={() => setShowAlumniMenu(false)}
                      >
                        <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 715.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <div>
                          <div className="font-medium">Alumni Directory</div>
                          <div className="text-xs text-gray-500">Browse fellow graduates</div>
                        </div>
                      </a>
                      <a
                        href={PATHS.messages}
                        className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        onClick={() => setShowAlumniMenu(false)}
                      >
                        <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                        <div>
                          <div className="font-medium">My Messages</div>
                          <div className="text-xs text-gray-500">Connect with alumni</div>
                        </div>
                      </a>
                      <a
                        href={PATHS.groups}
                        className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        onClick={() => setShowAlumniMenu(false)}
                      >
                        <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 715.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 616 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <div>
                          <div className="font-medium">Alumni Groups</div>
                          <div className="text-xs text-gray-500">Join interest groups</div>
                        </div>
                      </a>
                      {profile?.can_access_news_curation && (
                        <a
                          href={PATHS.newsCuration}
                          className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          onClick={() => setShowAlumniMenu(false)}
                        >
                          <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                          </svg>
                          <div>
                            <div className="font-medium">Content Curation</div>
                            <div className="text-xs text-gray-500">Share news & videos</div>
                          </div>
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Logout Button - Gray */}
              <button
                onClick={handleLogout}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Mobile Layout */}
      <div className="md:hidden pb-20"> {/* pb-20 for bottom nav space */}
        <div className="px-4 py-4 space-y-4">
          {/* Mobile Alert Messages */}
          {!profile?.email_verified && (
            <div className="rounded-lg bg-yellow-50 p-3 border-l-4 border-yellow-400">
              <div className="flex items-start">
                <svg className="h-4 w-4 text-yellow-400 mt-0.5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div className="ml-2">
                  <h3 className="text-sm font-medium text-yellow-800">Verify Email</h3>
                  <p className="mt-1 text-xs text-yellow-700">Check your email for verification link</p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-lg bg-red-50 p-3 border-l-4 border-red-400">
              <div className="flex items-start">
                <FiX className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                <p className="ml-2 text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}

          {successMessage && (
            <div className="rounded-lg bg-green-50 p-3 border-l-4 border-green-400">
              <div className="flex items-start">
                <FiCheckCircle className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                <p className="ml-2 text-sm text-green-800">{successMessage}</p>
              </div>
            </div>
          )}

          {/* Mobile Profile Card */}
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center space-x-3">
              <div className="relative">
                {profile?.headshot_url ? (
                  <img
                    src={profile.headshot_url}
                    alt="Profile"
                    className="h-16 w-16 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center">
                    <FiUser className="h-8 w-8 text-gray-400" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-900">
                  {profile?.first_name} {profile?.last_name}
                </h2>
                {profile?.current_position && (
                  <p className="text-sm text-gray-600">{profile?.current_position}</p>
                )}
                {profile?.current_employer && (
                  <p className="text-sm text-gray-500">{profile?.current_employer}</p>
                )}
              </div>
            </div>
            
            {/* Mobile Quick Actions */}
            <div className="mt-4 grid grid-cols-3 gap-2">
              {/* Connection Requests - Mobile Quick Action */}
              {privacySettings.social_networking_enabled && (
                <button
                  onClick={scrollToConnectionRequests}
                  className="relative flex items-center justify-center px-2 py-2 bg-blue-100 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-200 transition-colors"
                >
                  <FiUserPlus className="h-4 w-4 mr-1" />
                  Requests
                  {pendingConnectionCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full min-w-[1.25rem] flex items-center justify-center">
                      {pendingConnectionCount > 99 ? '99+' : pendingConnectionCount}
                    </span>
                  )}
                </button>
              )}
              
              <button
                onClick={() => navigate(PATHS.messages)}
                className="relative flex items-center justify-center px-2 py-2 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200 transition-colors"
              >
                <FiMail className="h-4 w-4 mr-1" />
                Messages
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full min-w-[1.25rem] flex items-center justify-center">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
              
              <button
                onClick={() => navigate(PATHS.jobBoard)}
                className="flex items-center justify-center px-2 py-2 bg-purple-100 text-purple-700 rounded-lg text-xs font-medium hover:bg-purple-200 transition-colors"
              >
                <FiBriefcase className="h-4 w-4 mr-1" />
                Jobs
              </button>
            </div>
          </div>

          {/* Mobile Profile Status */}
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-900">Profile Status</h3>
              <button
                onClick={() => navigate(PATHS.profileManagement)}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                Manage
              </button>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Email</span>
                <div className="flex items-center space-x-1">
                  {profile?.email_verified ? (
                    <>
                      <FiCheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-xs text-green-600">Verified</span>
                    </>
                  ) : (
                    <>
                      <svg className="h-4 w-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <span className="text-xs text-yellow-600">Pending</span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Profile</span>
                <div className="flex items-center space-x-1">
                  {(() => {
                    const hasPhoto = !!profile?.headshot_url;
                    const hasResume = !!profile?.resume_url;
                    const hasLinkedIn = !!(profile?.skills_list && profile?.skills_list.length > 0);
                    const hasVerifiedEmail = !!profile?.email_verified;
                    const isComplete = hasPhoto && hasResume && hasLinkedIn && hasVerifiedEmail;
                    return isComplete ? (
                      <>
                        <FiCheckCircle className="h-4 w-4 text-green-500" />
                        <span className="text-xs text-green-600">Complete</span>
                      </>
                    ) : (
                      <>
                        <svg className="h-4 w-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <span className="text-xs text-blue-600">Incomplete</span>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Connection Management Section */}
          {privacySettings.social_networking_enabled && (
            <div className="space-y-4" data-section="mobile-connection-requests">
              {connectionError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-800">{connectionError}</p>
                    </div>
                    <div className="ml-auto pl-3">
                      <button
                        onClick={() => setConnectionError('')}
                        className="inline-flex text-red-400 hover:text-red-600"
                      >
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Mobile Pending Connection Requests */}
              {pendingConnections.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm">
                  <div className="px-4 py-3 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center">
                      <svg className="h-5 w-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                      </svg>
                      Connection Requests
                      <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {pendingConnections.length}
                      </span>
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Alumni who would like to connect with you
                    </p>
                  </div>
                  <div className="p-4">
                    <div className="space-y-4">
                      {loadingConnections ? (
                        <div className="flex justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                      ) : (
                        pendingConnections.map((connection) => (
                          <div key={connection.id} className="border border-gray-200 rounded-lg p-3">
                            <div className="flex items-center space-x-3 mb-3">
                              {connection.headshot_url ? (
                                <img
                                  src={connection.headshot_url}
                                  alt={connection.requester_name}
                                  className="h-10 w-10 rounded-full object-cover"
                                />
                              ) : (
                                <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                  </svg>
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-medium text-gray-900 truncate">{connection.requester_name}</h4>
                                <p className="text-xs text-gray-500">
                                  {connection.current_position && connection.current_employer
                                    ? `${connection.current_position} at ${connection.current_employer}`
                                    : connection.current_employer || connection.major || 'Alumni'
                                  }
                                </p>
                                {connection.connection_type && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mt-1">
                                    {connection.connection_type}
                                  </span>
                                )}
                              </div>
                            </div>
                            {connection.message && (
                              <p className="text-sm text-gray-600 mb-3 italic">"{connection.message}"</p>
                            )}
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleConnectionResponse(connection.id, 'accepted')}
                                className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                              >
                                Accept
                              </button>
                              <button
                                onClick={() => handleConnectionResponse(connection.id, 'declined')}
                                className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              >
                                Decline
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Social Networking Features for Mobile Sidebar */}
              {privacySettings.social_networking_enabled && profile && (
                <div className="space-y-4">
                  {/* Post Creator */}
                  <PostCreator onPostCreated={handlePostCreated} currentUser={profile} />
                  
                  {/* News Feed */}
                  <div data-feed-section>
                    <AlumniNewsFeed 
                      currentUser={profile} 
                      onError={handleFeedError}
                      newPostData={newPostForFeed}
                    />
                  </div>
                  
                  {/* People You May Know - Collapsible */}
                  <div className="mt-6 bg-white rounded-lg shadow-sm">
                    <div 
                      className="px-4 py-3 border-b border-gray-200 flex items-center justify-between cursor-pointer"
                      onClick={() => setIsPeopleYouMayKnowCollapsed(!isPeopleYouMayKnowCollapsed)}
                    >
                      <h3 className="text-sm font-medium text-gray-900 flex items-center">
                        <FiUserPlus className="h-4 w-4 mr-2 text-blue-500" />
                        People You May Know
                      </h3>
                      <div className="transform transition-transform duration-200">
                        {isPeopleYouMayKnowCollapsed ? (
                          <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        ) : (
                          <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                        )}
                      </div>
                    </div>
                    {!isPeopleYouMayKnowCollapsed && (
                      <div className="px-4 py-4">
                        <PeopleYouMayKnow />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Mobile My Connections */}
              {myConnections.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm">
                  <div 
                    className="px-4 py-3 border-b border-gray-200 flex items-center justify-between cursor-pointer"
                    onClick={() => setIsMyConnectionsCollapsed(!isMyConnectionsCollapsed)}
                  >
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <div>
                        <h3 className="text-sm font-medium text-gray-900 flex items-center">
                          My Connections
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {myConnections.length}
                          </span>
                        </h3>
                        <p className="text-xs text-gray-500">Your professional network</p>
                      </div>
                    </div>
                    <div className="transform transition-transform duration-200">
                      {isMyConnectionsCollapsed ? (
                        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                  {!isMyConnectionsCollapsed && (
                    <div className="p-4">
                    <div className="space-y-3">
                      {loadingConnections ? (
                        <div className="flex justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                      ) : (
                        myConnections.map((connection) => (
                          <div key={connection.id} className="border border-gray-200 rounded-lg p-3">
                            <div className="flex items-center space-x-3 mb-3">
                              {connection.other_user_headshot_url ? (
                                <img
                                  src={connection.other_user_headshot_url}
                                  alt={connection.other_user_name}
                                  className="h-10 w-10 rounded-full object-cover"
                                />
                              ) : (
                                <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                  </svg>
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <h4 className="text-sm font-medium text-gray-900 truncate">
                                    {connection.other_user_name}
                                  </h4>
                                  <button
                                    onClick={() => handleRemoveConnection(connection.id)}
                                    className="text-red-400 hover:text-red-600 focus:outline-none p-1"
                                    title="Remove connection"
                                  >
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                </div>
                                <p className="text-xs text-gray-500">
                                  {connection.other_user_current_position && connection.other_user_current_employer
                                    ? `${connection.other_user_current_position} at ${connection.other_user_current_employer}`
                                    : connection.other_user_current_employer || connection.other_user_university || 'Alumni'
                                  }
                                </p>
                                {connection.connection_type && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                                    {connection.connection_type}
                                  </span>
                                )}
                                <p className="text-xs text-gray-400 mt-1">
                                  Connected {new Date(connection.connected_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => navigate(`/alumni/profile/${connection.other_user_id}`)}
                                className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              >
                                View Profile
                              </button>
                              <button
                                onClick={() => navigate(PATHS.messages, { 
                                  state: { preselectedRecipientId: connection.other_user_id, preselectedRecipientName: connection.other_user_name }
                                })}
                                className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              >
                                Message
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    </div>
                  )}
                </div>
              )}

              {/* Mobile Empty State */}
              {!loadingConnections && pendingConnections.length === 0 && myConnections.length === 0 && (
                <div className="bg-white rounded-lg shadow-sm">
                  <div className="px-4 py-8 text-center">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No connections yet</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Start building your professional network by connecting with fellow alumni.
                    </p>
                    <div className="mt-6">
                      <button
                        onClick={() => navigate(PATHS.directory)}
                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        Browse Alumni Directory
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </div>

      {/* WebSocket Live Connection Indicator */}
      {privacySettings.social_networking_enabled && (
        <div className="fixed top-20 right-4 z-50 flex flex-col items-end space-y-2">
          {/* Connection Status */}
          <div className={`flex items-center space-x-2 px-3 py-1 rounded-full shadow-md transition-all ${
            isConnected ? 'bg-green-100' : wsConnectionError ? 'bg-red-100' : 'bg-yellow-100'
          }`}>
            <div className={`w-2 h-2 rounded-full ${
              isConnected ? 'bg-green-500 animate-pulse' : wsConnectionError ? 'bg-red-500' : 'bg-yellow-500 animate-pulse'
            }`} />
            <span className={`text-xs font-medium ${
              isConnected ? 'text-green-700' : wsConnectionError ? 'text-red-700' : 'text-yellow-700'
            }`}>
              {isConnected ? 'Live' : wsConnectionError ? 'Disconnected' : 'Connecting...'}
            </span>
          </div>
          
          {/* New Post Notification */}
          {newPostNotification && (
            <div className="bg-blue-500 text-white px-4 py-3 rounded-lg shadow-lg max-w-sm animate-slide-in relative">
              {/* Close button */}
              <button
                onClick={() => setNewPostNotification(null)}
                className="absolute top-2 right-2 text-white hover:text-gray-200"
                title="Dismiss notification"
              >
                <FiX className="h-4 w-4" />
              </button>
              
              <div className="flex items-start space-x-3 pr-6">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">New post from {newPostNotification.author_name}</p>
                  <p className="text-xs mt-1 opacity-90">{newPostNotification.content}</p>
                  <div className="flex space-x-3 mt-2">
                    <button
                      onClick={() => {
                        setNewPostNotification(null);
                        // Scroll to feed and refresh it
                        const feedElement = document.querySelector('[data-feed-section]');
                        if (feedElement) {
                          feedElement.scrollIntoView({ behavior: 'smooth' });
                        }
                        // Trigger feed refresh
                        handleFeedRefresh();
                      }}
                      className="text-xs underline hover:no-underline"
                    >
                      View in feed
                    </button>
                    <button
                      onClick={() => handleFeedRefresh()}
                      className="text-xs underline hover:no-underline"
                    >
                      Refresh feed
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Desktop Main Content */}
      <main className="hidden md:block max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Messages */}
          {!profile?.email_verified && (
            <div className="mb-4 rounded-md bg-yellow-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">Email Verification Required</h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>Please check your email and click the verification link to complete your account setup. If you didn't receive the email, you can <a href={`${PATHS.verifyEmail}/invalid`} className="font-medium text-yellow-800 underline hover:text-yellow-900">request a new verification email here</a>.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-4 rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {successMessage && (
            <div className="mb-4 rounded-md bg-green-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-800">{successMessage}</p>
                </div>
              </div>
            </div>
          )}

          {/* Profile Status Indicators */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center space-x-2">
                <h4 className="text-sm font-medium text-gray-700">Profile Status</h4>
                <div className="flex items-center space-x-3">
                  {/* Email Verification */}
                  <div className="flex items-center space-x-1">
                    {profile?.email_verified ? (
                      <>
                        <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-xs text-green-600 font-medium">Email Verified</span>
                      </>
                    ) : (
                      <>
                        <svg className="h-4 w-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <span className="text-xs text-yellow-600 font-medium">Email Pending</span>
                      </>
                    )}
                  </div>

                  {/* Profile Completeness */}
                  <div className="flex items-center space-x-1">
                    {(() => {
                      // Use same completion criteria as the progress bar and popup
                      const hasPhoto = !!profile?.headshot_url;
                      const hasResume = !!profile?.resume_url;
                      const hasLinkedIn = !!(profile?.skills_list && profile?.skills_list.length > 0);
                      const hasVerifiedEmail = !!profile?.email_verified;
                      const isProfileComplete = hasPhoto && hasResume && hasLinkedIn && hasVerifiedEmail;
                      return isProfileComplete ? (
                        <>
                          <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span className="text-xs text-green-600 font-medium">Profile Complete</span>
                        </>
                      ) : (
                        <>
                          <svg className="h-4 w-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          <span className="text-xs text-blue-600 font-medium">Profile Incomplete</span>
                        </>
                      );
                    })()}
                  </div>

                  {/* Resume Status */}
                  <div className="flex items-center space-x-1">
                    {profile?.resume_url ? (
                      <>
                        <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                        <span className="text-xs text-green-600 font-medium">Resume Uploaded</span>
                      </>
                    ) : (
                      <>
                        <svg className="h-4 w-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2h12v8H4V6z" clipRule="evenodd" />
                        </svg>
                        <span className="text-xs text-gray-500 font-medium">No Resume</span>
                      </>
                    )}
                  </div>

                  {/* Headshot Status */}
                  <div className="flex items-center space-x-1">
                    {profile?.headshot_url ? (
                      <>
                        <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                        </svg>
                        <span className="text-xs text-green-600 font-medium">Photo Uploaded</span>
                      </>
                    ) : (
                      <>
                        <svg className="h-4 w-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                        </svg>
                        <span className="text-xs text-gray-500 font-medium">No Photo</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Profile Completion Progress */}
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-500">Completion:</span>
                <div className="w-16 bg-gray-200 rounded-full h-2">
                  {(() => {
                    // Calculate completion based on key profile fields
                    const fields = [
                      !!profile?.email_verified,
                      !!profile?.headshot_url,
                      !!profile?.resume_url,
                      !!(profile?.skills_list && profile?.skills_list.length > 0) // LinkedIn import
                    ];
                    const completedCount = fields.filter(Boolean).length;
                    const percentage = (completedCount / fields.length) * 100;
                    
                    return (
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${
                          percentage === 100 ? 'bg-green-500' : 'bg-blue-500'
                        }`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    );
                  })()}
                </div>
                <span className="text-xs text-gray-600 font-medium">
                  {(() => {
                    const fields = [
                      !!profile?.email_verified,
                      !!profile?.headshot_url,
                      !!profile?.resume_url,
                      !!(profile?.skills_list && profile?.skills_list.length > 0) // LinkedIn import
                    ];
                    const completedCount = fields.filter(Boolean).length;
                    return Math.round((completedCount / fields.length) * 100);
                  })()}%
                </span>
              </div>
            </div>
          </div>

          {/* Main Content Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
            {/* Left Sidebar - Headshot */}
            <div className="lg:col-span-1">
              <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:px-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                    Profile Photo
                    <button
                      onClick={() => headshotInputRef.current?.click()}
                      className="ml-2 p-1 rounded-full hover:bg-gray-100 transition-colors"
                      title="Upload new photo"
                    >
                      <FiEdit3 className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    </button>
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Your professional headshot
                  </p>
                </div>
                <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                  {/* Current Headshot Display */}
                  <div className="flex flex-col items-center space-y-4">
                    {profile?.headshot_url ? (
                      <img
                        src={profile.headshot_url}
                        alt="Profile headshot"
                        className="h-40 w-40 object-cover rounded-full border-4 border-gray-200 shadow-lg"
                      />
                    ) : (
                      <div className="h-40 w-40 bg-gray-200 rounded-full flex items-center justify-center border-4 border-gray-300">
                        <svg className="h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                    )}
                    
                    <form onSubmit={handleHeadshotUpload} className="w-full space-y-3">
                      <div>
                        <input
                          ref={headshotInputRef}
                          type="file"
                          accept=".jpg,.jpeg,.png"
                          onChange={(e) => setHeadshotFile(e.target.files[0])}
                          className="block w-full text-sm text-gray-500
                            file:mr-2 file:py-2 file:px-3
                            file:rounded-md file:border-0
                            file:text-xs file:font-medium
                            file:bg-blue-50 file:text-blue-700
                            hover:file:bg-blue-100"
                        />
                        <p className="mt-1 text-xs text-gray-500">JPG, JPEG, PNG - Max 2MB</p>
                      </div>
                      <button
                        type="submit"
                        disabled={!headshotFile || isUploadingHeadshot}
                        className={`w-full px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                          !headshotFile || isUploadingHeadshot
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700'
                        }`}
                      >
                        {isUploadingHeadshot ? 'Uploading...' : 'Upload Photo'}
                      </button>
                    </form>
                  </div>
                </div>

                {/* Key Action Buttons */}
                <div id="profile-actions" className="mt-6 space-y-3">
                  <div className="px-4 pb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Complete Your Profile</h4>
                    
                    {/* Resume Upload Button - Now Purple */}
                    <button
                      onClick={() => navigate(PATHS.profileManagement + '#resume-section')}
                      className={`w-full flex items-center justify-center px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white transition-colors mb-3 ${
                        profile?.resume_url 
                          ? 'bg-purple-500 hover:bg-purple-600' 
                          : 'bg-purple-600 hover:bg-purple-700'
                      }`}
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {profile?.resume_url ? 'Update Resume' : 'Upload Resume'}
                    </button>

                    {/* LinkedIn Import Button - Now Blue */}
                    <button
                      onClick={() => navigate(PATHS.profileManagement + '#linkedin-section')}
                      className={`w-full flex items-center justify-center px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white transition-colors ${
                        profile?.skills_list && profile.skills_list.length > 0
                          ? 'bg-blue-500 hover:bg-blue-600'
                          : 'bg-blue-600 hover:bg-blue-700'
                      }`}
                    >
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                      {profile?.skills_list && profile.skills_list.length > 0 ? 'Update LinkedIn Profile' : 'Import LinkedIn Profile'}
                    </button>
                  </div>

                </div>
              </div>

              {/* Profile Background Section */}
              <div className="bg-white shadow overflow-hidden sm:rounded-lg mt-6">
                <div className="px-4 py-5 sm:px-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Profile Background</h4>
                  <p className="text-xs text-gray-500 mb-4">
                    Upload a custom background image for your profile page. Recommended dimensions: 1920x400px
                  </p>
                  
                  {/* Background Upload Form */}
                  <form onSubmit={handleBackgroundUpload} className="space-y-4">
                    <div>
                      <input
                        type="file"
                        ref={backgroundInputRef}
                        onChange={(e) => setBackgroundFile(e.target.files[0])}
                        accept="image/*"
                        className="hidden"
                        id="background-upload"
                      />
                      <label
                        htmlFor="background-upload"
                        className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Choose Background Image
                      </label>
                      {backgroundFile && (
                        <span className="ml-3 text-sm text-gray-600">
                          {backgroundFile.name}
                        </span>
                      )}
                    </div>

                    {backgroundFile && (
                      <button
                        type="submit"
                        disabled={isUploadingBackground}
                        className={`w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white transition-colors ${
                          isUploadingBackground
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-indigo-600 hover:bg-indigo-700'
                        }`}
                      >
                        {isUploadingBackground ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Uploading...
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                            Upload Background
                          </>
                        )}
                      </button>
                    )}

                    {profile?.background_image_url && (
                      <div className="mt-3">
                        <p className="text-sm text-gray-600 mb-2">Current background:</p>
                        <div className="relative h-24 rounded-lg overflow-hidden border border-gray-200">
                          <img
                            src={profile.background_image_url}
                            alt="Current background"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>

            {/* Right Content - Social Feed */}
            <div className="lg:col-span-3">
              {/* Post Creation and News Feed */}
              <PostCreator onPostCreated={handlePostCreated} currentUser={profile} />
              <div data-feed-section>
                <AlumniNewsFeed 
                  currentUser={profile} 
                  onError={handleFeedError}
                  newPostData={newPostForFeed}
                />
              </div>
            </div>
          </div>

          {/* Profile Information - Moved Below Grid */}
          <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                  Profile Information
                  <button
                    onClick={() => setIsEditing(true)}
                    className="ml-2 p-1 rounded-full hover:bg-gray-100 transition-colors"
                    title="Edit profile"
                  >
                    <FiEdit3 className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                  </button>
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Personal details and professional information
                </p>
              </div>
              {!isEditing && (
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowPrivacySettings(true)}
                    className="px-4 py-2 border border-blue-300 rounded-md text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100"
                  >
                    Privacy Settings
                  </button>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Edit Profile
                  </button>
                  <button
                    onClick={() => navigate(`/alumni/profile/${profile?.id}`)}
                    className="px-4 py-2 border border-green-300 rounded-md text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100"
                  >
                    {IS_TLA ? 'View TLA Profile' : 'View NACME Profile'}
                  </button>
                </div>
              )}
            </div>
            
            <div className="border-t border-gray-200">
              {isEditing ? (
                <form onSubmit={handleUpdateProfile} className="px-4 py-5 sm:p-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">First Name</label>
                      <input
                        type="text"
                        name="first_name"
                        value={editData.first_name}
                        onChange={handleEditChange}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Last Name</label>
                      <input
                        type="text"
                        name="last_name"
                        value={editData.last_name}
                        onChange={handleEditChange}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        value={editData.phone}
                        onChange={handleEditChange}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">University</label>
                      <input
                        type="text"
                        name="university"
                        value={editData.university}
                        onChange={handleEditChange}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Degree Type</label>
                      <select
                        name="degree_type"
                        value={editData.degree_type}
                        onChange={handleEditChange}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      >
                        {degreeTypes.map(type => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Major</label>
                      <input
                        type="text"
                        name="major"
                        value={editData.major}
                        onChange={handleEditChange}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Graduation Year</label>
                      <input
                        type="number"
                        name="graduation_year"
                        value={editData.graduation_year}
                        onChange={handleEditChange}
                        min="1950"
                        max="2030"
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Current Employer</label>
                      <input
                        type="text"
                        name="current_employer"
                        value={editData.current_employer}
                        onChange={handleEditChange}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Current Position</label>
                      <input
                        type="text"
                        name="current_position"
                        value={editData.current_position}
                        onChange={handleEditChange}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Years of Experience</label>
                      <input
                        type="number"
                        name="years_experience"
                        value={editData.years_experience}
                        onChange={handleEditChange}
                        min="0"
                        max="50"
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">LinkedIn URL</label>
                      <input
                        type="url"
                        name="linkedin_url"
                        value={editData.linkedin_url}
                        onChange={handleEditChange}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">About</label>
                      <textarea
                        name="about"
                        value={editData.about}
                        onChange={handleEditChange}
                        rows={4}
                        placeholder="Tell us about yourself, your professional journey, interests, and goals..."
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setIsEditing(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                    >
                      Save Changes
                    </button>
                  </div>
                </form>
              ) : (
                <dl>
                  {profile && (
                    <>
                      <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt className="text-sm font-medium text-gray-500">Full name</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                          {profile.first_name} {profile.last_name}
                        </dd>
                      </div>
                      <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt className="text-sm font-medium text-gray-500">Email address</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                          {profile.email}
                        </dd>
                      </div>
                      <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt className="text-sm font-medium text-gray-500">Phone</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                          {profile.phone || 'Not provided'}
                        </dd>
                      </div>
                      {profile.location_data && (
                        <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                          <dt className="text-sm font-medium text-gray-500">Location</dt>
                          <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                            <LocationDisplay locationData={profile.location_data} />
                          </dd>
                        </div>
                      )}
                      <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt className="text-sm font-medium text-gray-500">Education</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                          {profile.degree_type && degreeTypes.find(d => d.value === profile.degree_type)?.label}
                          {profile.major && ` in ${profile.major}`}
                          {profile.university && ` from ${profile.university}`}
                          {profile.graduation_year && ` (${profile.graduation_year})`}
                          {!profile.degree_type && !profile.major && !profile.university && 'Not provided'}
                        </dd>
                      </div>
                      <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt className="text-sm font-medium text-gray-500">Current Employment</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                          {profile.current_position && profile.current_employer 
                            ? `${profile.current_position} at ${profile.current_employer}`
                            : profile.current_position || profile.current_employer || 'Not provided'}
                        </dd>
                      </div>
                      <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt className="text-sm font-medium text-gray-500">Experience</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                          {profile.years_experience ? `${profile.years_experience} years` : 'Not provided'}
                        </dd>
                      </div>
                      <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt className="text-sm font-medium text-gray-500">LinkedIn</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                          {profile.linkedin_url ? (
                            <a href={profile.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-500">
                              View Profile
                            </a>
                          ) : (
                            'Not provided'
                          )}
                        </dd>
                      </div>
                      <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt className="text-sm font-medium text-gray-500">About</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                          {profile.about ? (
                            <div className="whitespace-pre-wrap">{profile.about}</div>
                          ) : (
                            'Not provided'
                          )}
                        </dd>
                      </div>
                    </>
                  )}
                </dl>
              )}
            </div>
          </div>

          {/* Rich LinkedIn Data Sections */}
          {profile && (profile.skills_list || profile.experience_history || profile.education_history) && (
            <>
              {/* Skills Section - Now Editable */}
              <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:px-6">
                  <EditableSkills 
                    onSkillsChange={() => {
                      // Refresh profile when skills change
                      fetchProfile();
                    }}
                    readOnly={false}
                  />
                </div>
              </div>

              {/* Experience Section */}
              {profile.experience_history && profile.experience_history.length > 0 && (
                <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
                  <div className="px-4 py-5 sm:px-6">
                    <ExperienceDisplay experienceHistory={profile.experience_history} />
                  </div>
                </div>
              )}

              {/* Education Section */}
              {profile.education_history && profile.education_history.length > 0 && (
                <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
                  <div className="px-4 py-5 sm:px-6">
                    <EducationDisplay educationHistory={profile.education_history} />
                  </div>
                </div>
              )}
            </>
          )}

          {/* Connection Management Section */}
          {privacySettings.social_networking_enabled && (
            <div className="mt-8 space-y-6" data-section="desktop-connection-requests">
              {connectionError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-800">{connectionError}</p>
                    </div>
                    <div className="ml-auto pl-3">
                      <button
                        onClick={() => setConnectionError('')}
                        className="inline-flex text-red-400 hover:text-red-600"
                      >
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Pending Connection Requests */}
              {pendingConnections.length > 0 && (
                <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                  <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                      <svg className="h-5 w-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                      </svg>
                      Connection Requests
                      <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {pendingConnections.length}
                      </span>
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Alumni who would like to connect with you
                    </p>
                  </div>
                  <div className="px-4 py-5 sm:p-6">
                    <div className="space-y-4">
                      {loadingConnections ? (
                        <div className="flex justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                      ) : (
                        pendingConnections.map((connection) => (
                          <div key={connection.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                            <div className="flex items-center space-x-4">
                              {connection.headshot_url ? (
                                <img
                                  src={connection.headshot_url}
                                  alt={connection.requester_name}
                                  className="h-12 w-12 rounded-full object-cover"
                                />
                              ) : (
                                <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center">
                                  <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                  </svg>
                                </div>
                              )}
                              <div>
                                <h4 className="text-sm font-medium text-gray-900">{connection.requester_name}</h4>
                                <p className="text-sm text-gray-500">
                                  {connection.current_position && connection.current_employer
                                    ? `${connection.current_position} at ${connection.current_employer}`
                                    : connection.current_employer || connection.major || 'Alumni'
                                  }
                                </p>
                                {connection.connection_type && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mt-1">
                                    {connection.connection_type}
                                  </span>
                                )}
                                {connection.message && (
                                  <p className="text-sm text-gray-600 mt-2 italic">"{connection.message}"</p>
                                )}
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleConnectionResponse(connection.id, 'accepted')}
                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                              >
                                Accept
                              </button>
                              <button
                                onClick={() => handleConnectionResponse(connection.id, 'declined')}
                                className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              >
                                Decline
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* My Connections */}
              {myConnections.length > 0 && (
                <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                  <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                      <svg className="h-5 w-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      My Connections
                      <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {myConnections.length}
                      </span>
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Your professional network
                    </p>
                  </div>
                  <div className="px-4 py-5 sm:p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {loadingConnections ? (
                        <div className="col-span-2 flex justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                      ) : (
                        myConnections.map((connection) => (
                          <div key={connection.id} className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
                            <div className="flex items-center space-x-3">
                              {connection.other_user_headshot_url ? (
                                <img
                                  src={connection.other_user_headshot_url}
                                  alt={connection.other_user_name}
                                  className="h-10 w-10 rounded-full object-cover"
                                />
                              ) : (
                                <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                  </svg>
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <h4 className="text-sm font-medium text-gray-900 truncate">
                                    {connection.other_user_name}
                                  </h4>
                                  <button
                                    onClick={() => handleRemoveConnection(connection.id)}
                                    className="text-red-400 hover:text-red-600 focus:outline-none"
                                    title="Remove connection"
                                  >
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                </div>
                                <p className="text-xs text-gray-500">
                                  {connection.other_user_current_position && connection.other_user_current_employer
                                    ? `${connection.other_user_current_position} at ${connection.other_user_current_employer}`
                                    : connection.other_user_current_employer || connection.other_user_university || 'Alumni'
                                  }
                                </p>
                                {connection.connection_type && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                                    {connection.connection_type}
                                  </span>
                                )}
                                <p className="text-xs text-gray-400 mt-1">
                                  Connected {new Date(connection.connected_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div className="mt-3 flex space-x-2">
                              <button
                                onClick={() => navigate(`/alumni/profile/${connection.other_user_id}`)}
                                className="flex-1 inline-flex justify-center items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              >
                                View Profile
                              </button>
                              <button
                                onClick={() => navigate(PATHS.messages, { 
                                  state: { preselectedRecipientId: connection.other_user_id, preselectedRecipientName: connection.other_user_name }
                                })}
                                className="flex-1 inline-flex justify-center items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              >
                                Message
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Empty State */}
              {!loadingConnections && pendingConnections.length === 0 && myConnections.length === 0 && (
                <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                  <div className="px-4 py-5 sm:p-6 text-center">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No connections yet</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Start building your professional network by connecting with fellow alumni.
                    </p>
                    <div className="mt-6">
                      <button
                        onClick={() => navigate(PATHS.directory)}
                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        Browse Alumni Directory
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Upgrade Success Banner */}
          {showUpgradeBanner && (
            <div className="mt-8 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FiCheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-green-800">Payment received — thank you!</p>
                  <p className="text-sm text-green-700">Your membership upgrade is being processed. You'll receive a confirmation email shortly and your new capabilities will be activated within 24 hours.</p>
                </div>
              </div>
              <button onClick={() => setShowUpgradeBanner(false)} className="text-green-600 hover:text-green-800 flex-shrink-0 ml-4">
                <FiX className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Executive Introductions Section */}
          {(
            <div className="mt-8">
              <div className="bg-white rounded-lg shadow-sm">
                <div
                  className="px-4 py-3 border-b border-gray-200 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                  onClick={() => setIsBrokerageCollapsed(!isBrokerageCollapsed)}
                >
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <svg className="h-5 w-5 mr-2 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    Executive Introductions
                  </h3>
                  <div className="transform transition-transform duration-200">
                    {isBrokerageCollapsed ? (
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    )}
                  </div>
                </div>
                {!isBrokerageCollapsed && (
                  <div className="p-4">
                    <AlumniBrokeragePanel alumniToken={alumniToken} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* People You May Know Section */}
          {privacySettings.social_networking_enabled && (
            <div className="mt-8">
              <PeopleYouMayKnow />
            </div>
          )}


        </div>
      </main>

      {/* Profile Completion Popup */}
      {showCompletionPopup && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              {/* Header with Icon */}
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
                <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              
              {/* Title */}
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Complete Your Profile
              </h3>
              
              {/* Dynamic Message */}
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500 leading-relaxed">
                  {getProfileCompletionStatus().message}
                </p>
              </div>
              
              {/* Action Buttons */}
              <div className="items-center px-4 py-3 space-y-2">
                <button
                  onClick={() => {
                    setShowCompletionPopup(false);
                    // Scroll to the action buttons for easy access
                    document.getElementById('profile-actions')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="px-4 py-2 bg-blue-600 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300"
                >
                  Get Started
                </button>
                <button
                  onClick={() => setShowCompletionPopup(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 text-base font-medium rounded-md w-full shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  Maybe Later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Content View */}
      <main className="md:hidden">
        <div className="px-4 py-4 pb-20"> {/* pb-20 to account for bottom nav */}
          {/* Messages for mobile */}
          {!profile?.email_verified && (
            <div className="mb-4 rounded-md bg-yellow-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">Email Verification Required</h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>Please verify your email address to access all alumni features.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {successMessage && (
            <div className="mb-4 rounded-md bg-green-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <FiCheckCircle className="h-5 w-5 text-green-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-800">{successMessage}</p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-4 rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <FiX className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>{error}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Upgrade Success Banner - Mobile */}
          {showUpgradeBanner && (
            <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3 flex items-start justify-between">
              <div className="flex items-start gap-2">
                <FiCheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-green-800 text-sm">Payment received — thank you!</p>
                  <p className="text-xs text-green-700 mt-1">Your upgrade is being processed. You'll receive a confirmation email shortly.</p>
                </div>
              </div>
              <button onClick={() => setShowUpgradeBanner(false)} className="text-green-600 hover:text-green-800 flex-shrink-0 ml-2">
                <FiX className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Executive Introductions Section - Mobile */}
          {(
            <div className="mb-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <button
                  onClick={() => setIsBrokerageCollapsed(!isBrokerageCollapsed)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                      <FiUsers className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">Executive Introductions</span>
                  </div>
                  <FiChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${isBrokerageCollapsed ? '' : 'rotate-180'}`} />
                </button>
                {!isBrokerageCollapsed && (
                  <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                    <AlumniBrokeragePanel alumniToken={alumniToken} />
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </main>

      {/* Privacy Settings Modal */}
      {showPrivacySettings && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Privacy Settings</h3>
                <button
                  onClick={() => setShowPrivacySettings(false)}
                  className="text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="px-6 py-4 space-y-6">
              {/* Social Networking Toggle */}
              <div>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-900">Social Networking</h4>
                    <p className="text-sm text-gray-500">Enable alumni networking features (groups, directory, messages)</p>
                  </div>
                  <div className="ml-4">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        value=""
                        className="sr-only peer"
                        checked={privacySettings.social_networking_enabled}
                        onChange={(e) => handlePrivacySettingChange('social_networking_enabled', e.target.checked)}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Profile Visibility Settings */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Profile Visibility</h4>
                <p className="text-sm text-gray-500 mb-4">Choose what information is visible to other alumni</p>
                
                <div className="space-y-4">
                  {[
                    { key: 'show_current_employer', label: 'Current Employer' },
                    { key: 'show_current_position', label: 'Current Position' },
                    { key: 'show_degree_type', label: 'Degree Type' },
                    { key: 'show_major', label: 'Major' },
                    { key: 'show_graduation_year', label: 'Graduation Year' },
                    { key: 'show_university', label: 'University' },
                    { key: 'show_years_experience', label: 'Years of Experience' },
                    { key: 'show_location', label: 'Location' },
                    { key: 'show_skills', label: 'Skills' },
                    { key: 'show_about', label: 'About Section' },
                    { key: 'show_work_experience', label: 'Work Experience' }
                  ].map((setting) => (
                    <div key={setting.key} className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">{setting.label}</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={privacySettings[setting.key]}
                          onChange={(e) => handlePrivacySettingChange(setting.key, e.target.checked)}
                        />
                        <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowPrivacySettings(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={savePrivacySettings}
                disabled={privacyLoading}
                className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                  privacyLoading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {privacyLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Bottom Navigation */}
      <MobileAlumniBottomNav 
        activeSection="dashboard"
        onNavigate={(path) => navigate(path)}
      />
    </div>
  );
};

export default AlumniDashboard;
